import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useRBAC } from "../../hooks/useRBAC";
import { fetchTracked, fetchTrackedHistory, formatDate, formatMoney, trackChain, untrackChain } from "./fusionApi";

/** A tiny trend line of daily means; days the chain was unavailable leave a gap, never a zero. */
export const Sparkline = ({ points, baseline, width = 220, height = 44 }) => {
  const values = points.map((p) => (p.available && p.mean != null ? Number(p.mean) : null));
  const known = values.filter((v) => v !== null).concat(baseline != null ? [Number(baseline)] : []);
  if (known.length === 0 || points.length < 2) return null;
  const min = Math.min(...known), max = Math.max(...known);
  const span = max - min || 1;
  const x = (i) => (i / (points.length - 1)) * (width - 4) + 2;
  const y = (v) => height - 4 - ((v - min) / span) * (height - 8);
  const segments = [];
  let current = [];
  values.forEach((v, i) => {
    if (v === null) { if (current.length) segments.push(current); current = []; }
    else current.push(`${x(i)},${y(v)}`);
  });
  if (current.length) segments.push(current);
  return (
    <svg width={width} height={height} role="img" aria-label="Connected exposure trend" className="overflow-visible">
      {baseline != null && <line x1="2" x2={width - 2} y1={y(Number(baseline))} y2={y(Number(baseline))} stroke="#a5b4fc" strokeDasharray="3 3" />}
      {segments.map((s, i) => <polyline key={i} points={s.join(" ")} fill="none" stroke="#4f46e5" strokeWidth="1.5" />)}
    </svg>
  );
};

const alertClass = (sev) =>
  sev === "HIGH" || sev === "CRITICAL" ? "bg-red-100 text-red-700" : sev === "MEDIUM" ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-800";

/**
 * Track chain (FUSION.md §11 F3). Live Now / After the Break Point fix / Reduction for everyone, where
 * `reduction` is the break-point's FAIR mean removed (FUSION.md §4.6.4; 0 without a break-point); for
 * users with full access, the saved tracking: baseline, latest daily snapshot, the regression
 * watch's open alert and the trend. Tracking figures use the organization's own view, which is why
 * a Partial-view user is not offered them; Track / Untrack need RISK_MANAGER+ at ORG. Only a listable
 * chain (risks from two or more modules) can be tracked, so Track is hidden when `listable` is false;
 * Stop tracking stays available for an anchor that is already tracked.
 */
const FusionTrackPanel = ({ exposure, reduction = 0, anchorType, anchorId, partialView, listable = true }) => {
  const apiCall = useApi();
  const { canManageOrganizationRisks } = useRBAC();
  const canManage = canManageOrganizationRisks();
  const [tracked, setTracked] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const now = exposure?.mean ?? 0;

  const load = useCallback(async () => {
    if (partialView || !anchorType) return;
    try {
      const list = (await fetchTracked(apiCall)) || [];
      const row = list.find((t) => t.anchorType === anchorType && t.anchorId === String(anchorId)) || null;
      setTracked(row);
      setHistory(row ? ((await fetchTrackedHistory(apiCall, row.id, 90))?.snapshots || []) : []);
    } catch {
      setTracked(null);
      setHistory([]);
    }
  }, [apiCall, anchorType, anchorId, partialView]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (tracked) await untrackChain(apiCall, tracked.id);
      else await trackChain(apiCall, { anchorType, anchorId: String(anchorId) });
      await load();
    } catch {
      setError(tracked ? "Tracking could not be stopped." : "This chain could not be tracked.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-theme-secondary p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">Track chain</span>
        {tracked?.activeAlertSeverity && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${alertClass(tracked.activeAlertSeverity)}`}>
            Exposure rise alert · {tracked.activeAlertSeverity}
          </span>
        )}
        {!partialView && canManage && (tracked || listable) && (
          <button onClick={toggle} disabled={busy}
            className={`ml-auto text-xs px-2.5 py-1 rounded-lg ${tracked ? "border border-border bg-theme-secondary text-content-secondary" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}>
            {tracked ? "Stop tracking" : "Track chain"}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-6">
        <div><div className="font-semibold text-content-primary">{formatMoney(now)}</div><div className="text-xs text-content-muted">Now</div></div>
        <div><div className="font-semibold text-content-primary">{formatMoney(now - reduction)}</div><div className="text-xs text-content-muted">After the Break Point fix</div></div>
        <div><div className="font-semibold text-indigo-700">{formatMoney(reduction)}</div><div className="text-xs text-content-muted">Reduction</div></div>
        {tracked && (
          <div><div className="font-semibold text-content-primary">{formatMoney(tracked.baselineMean)}</div><div className="text-xs text-content-muted">Baseline · {formatDate(tracked.baselineAt)}</div></div>
        )}
      </div>

      {tracked && (
        <div className="mt-2">
          <Sparkline points={history} baseline={tracked.baselineMean} />
          <div className="text-xs text-content-muted mt-1">
            Snapshot daily; an alert is raised when connected exposure rises 10% and $10K above the baseline.
            {" "}Tracked figures leave out private deals, because the alert is shared with the organization.
            {tracked.lastSnapshotAt && ` Last snapshot ${formatDate(tracked.lastSnapshotAt)}${tracked.lastAvailable ? "" : " — the chain was not available that day"}.`}
          </div>
        </div>
      )}
      {!tracked && (
        <div className="text-xs text-content-muted mt-2">
          {partialView
            ? "Live figures. Tracking is available to users who can see the whole organization."
            : !listable ? "Live figures. Only a chain with risks from two or more modules can be tracked."
            : canManage ? "Live figures. Track this chain to keep a daily history and be alerted when its exposure rises."
                        : "Live figures. A risk manager can track this chain to keep a daily history."}
        </div>
      )}
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
};

export default FusionTrackPanel;
